<template>
  <div
    class="p-6"
    @keydown="onKeydown">
    <!-- Title -->
    <div class="mb-5">
      <h2
        data-modal-title
        class="text-lg font-bold">
        {{ $t('W: Justér pris') }}
      </h2>
      <p class="mt-1 text-sm">{{ lineName }}</p>
    </div>

    <!-- Current catalogue price -->
    <div class="mb-5 rounded-lg border border-border-subtle px-4 py-3 text-sm">
      <div class="flex justify-between">
        <span>{{ $t('W: Oprindelig pris') }}</span>
        <span
          v-if="originalUnitPrice === null"
          class="font-medium text-feedback-error">
          {{ $t('W: Ukendt pris') }}
        </span>
        <span
          v-else
          class="font-medium">
          {{ formatAmount(originalUnitPrice) }}
        </span>
      </div>
      <div
        v-if="hasExistingOverride"
        class="mt-1 flex justify-between">
        <span>{{ $t('W: Nuværende justerede pris') }}</span>
        <span class="font-medium">{{ formatAmount(currentUnitPrice) }}</span>
      </div>
      <div
        v-if="overriddenByText"
        class="mt-2 border-t border-border-subtle pt-2 text-xs text-text-muted">
        {{ overriddenByText }}
      </div>
    </div>

    <!-- New price -->
    <div class="mb-5">
      <label
        for="override-unit-price"
        class="mb-1 block text-sm font-semibold">
        {{ $t('W: Ny stykpris') }}
      </label>
      <input
        id="override-unit-price"
        v-model="unitPrice"
        type="number"
        min="0"
        step="0.01"
        class="w-full rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-border-strong focus:outline-none" />
    </div>

    <!-- Reason -->
    <div class="mb-5">
      <label
        for="override-reason"
        class="mb-1 block text-sm font-semibold">
        {{ $t('W: Årsag') }}
      </label>
      <textarea
        id="override-reason"
        v-model="reason"
        rows="3"
        :placeholder="$t('W: f.eks. Aftalt pris med kunden, prisen mangler i betalers prissegment...')"
        class="w-full resize-none rounded-md border border-border-subtle px-3 py-2 text-sm focus:border-border-strong focus:outline-none" />
      <span class="mt-1 block text-xs">
        {{ $t('W: Årsagen gemmes på ordrelinjen som dokumentation for justeringen') }}
      </span>
    </div>

    <!-- Buttons -->
    <div class="flex justify-between gap-3">
      <UiBaseButton
        v-if="hasExistingOverride"
        :text="$t('W: Fjern justering')"
        theme="neutral"
        :isDisabled="isSaving"
        :isLoading="isClearing"
        @button-click="onClear" />
      <UiBaseButton
        class="ml-auto"
        :text="$t('W: Gem pris')"
        :isDisabled="isSubmitDisabled"
        :isLoading="isSaving"
        @button-click="onSubmit" />
    </div>
  </div>
</template>

<script lang="ts">
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useModalStore } from '@/stores/modalStore'
import type { PropType } from 'vue'

export default {
  name: 'OverrideLinePriceModal',

  emits: ['close'],

  props: {
    lineName: { type: String, required: true },
    /** The catalogue price this line would otherwise be billed at — `null` when the line's
     *  price was resolved from "unknown" rather than corrected from a real one. */
    originalUnitPrice: { type: [Number, null] as PropType<number | null>, required: true },
    /** What the line is billed at right now — differs from the original only when overridden. */
    currentUnitPrice: { type: Number, required: true },
    hasExistingOverride: { type: Boolean, default: false },
    currencySymbol: { type: String, default: '' },
    /** "Justeret af {name} — {reason}" — blank when the line has never been overridden. */
    overriddenByText: { type: String, default: '' },
    onSave: {
      type: Function as PropType<(unitPrice: number, reason: string) => void | Promise<void>>,
      required: true,
    },
    onClearOverride: {
      type: Function as PropType<() => void | Promise<void>>,
      required: true,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      modalEntryID: useModalStore().top?.id,

      // string | number: initialised as a string, but Vue's v-model on a native
      // type="number" input implicitly applies the .number cast, so any valid user
      // input turns this into a number. Never call string methods on it directly.
      unitPrice: '' as string | number,
      reason: '',
      isSaving: false,
      isClearing: false,
    }
  },

  computed: {
    parsedUnitPrice(): number {
      return Number(this.unitPrice)
    },

    isSubmitDisabled(): boolean {
      if (this.isSaving || this.isClearing) return true
      if (String(this.unitPrice).trim().length === 0) return true
      if (Number.isNaN(this.parsedUnitPrice) || this.parsedUnitPrice < 0) return true
      return this.parsedUnitPrice === this.currentUnitPrice
    },
  },

  methods: {
    onKeydown(event: KeyboardEvent): void {
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault()
        this.onSubmit()
      }
    },

    formatAmount(value: number): string {
      return `${value.toLocaleString('da-DK', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${this.currencySymbol}`.trim()
    },

    async onSubmit(): Promise<void> {
      if (this.isSubmitDisabled) return
      this.isSaving = true
      try {
        // The handler does its own error handling (toast + inline error on the view) and never
        // rethrows — awaiting it here is only for the loading state and closing afterwards.
        await this.onSave(this.parsedUnitPrice, this.reason.trim())
      } finally {
        this.isSaving = false
      }
      this.$emit('close')
    },

    async onClear(): Promise<void> {
      if (this.isSaving || this.isClearing) return
      this.isClearing = true
      try {
        await this.onClearOverride()
      } finally {
        this.isClearing = false
      }
      this.$emit('close')
    },
  },

  created() {
    this.unitPrice = String(this.currentUnitPrice)

    // A typed price plus the reason for overriding it. Registered after `created()` seeds
    // `unitPrice` from the current line, or the modal would open dirty.
    this.unsavedGuard.register({
      snapshot: () => ({ unitPrice: this.unitPrice, reason: this.reason }),
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
